/**
 * Create Job Page
 * Form for clients to create new job postings
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLanguage, API } from "@/context/AppContext";
import { Loader2 } from "lucide-react";

const CreateJobPage = () => {
  const { t, language } = useLanguage();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    city: '',
    budget_min: '',
    budget_max: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/categories`).then(res => setCategories(res.data));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/jobs`, {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        location: { city: formData.city, country: 'CH' },
        budget_min: formData.budget_min ? parseFloat(formData.budget_min) : null,
        budget_max: formData.budget_max ? parseFloat(formData.budget_max) : null
      }, { withCredentials: true });

      toast.success('Auftrag erstellt!');
      navigate('/dashboard/jobs');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl" data-testid="create-job-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
          {t('createJobTitle')}
        </h1>
        <p className="text-gray-500 mt-1">{t('describeNeed')}</p>
      </div>

      <Card className="border-gray-200">
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="category">{t('category')}</Label>
              <Select 
                value={formData.category} 
                onValueChange={(v) => setFormData({ ...formData, category: v })}
              >
                <SelectTrigger className="mt-1" data-testid="job-category-select">
                  <SelectValue placeholder={t('selectCategory')} />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat[`name_${language}`] || cat.name_en || cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="title">{t('title')}</Label>
              <Input
                id="title"
                placeholder={t('titlePlaceholder')}
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="mt-1"
                required
                data-testid="job-title-input"
              />
            </div>

            <div>
              <Label htmlFor="description">{t('description')}</Label>
              <Textarea
                id="description"
                placeholder={t('descPlaceholder')}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="mt-1 min-h-[120px]"
                required
                data-testid="job-description-input"
              />
            </div>

            <div>
              <Label htmlFor="city">{t('city')}</Label>
              <Input
                id="city"
                placeholder={t('cityPlaceholder')}
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="mt-1"
                required
                data-testid="job-city-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="budget_min">{t('budgetMin')}</Label>
                <Input
                  id="budget_min"
                  type="number"
                  placeholder={t('optional')}
                  value={formData.budget_min}
                  onChange={(e) => setFormData({ ...formData, budget_min: e.target.value })}
                  className="mt-1"
                  data-testid="job-budget-min-input"
                />
              </div>
              <div>
                <Label htmlFor="budget_max">{t('budgetMax')}</Label>
                <Input
                  id="budget_max"
                  type="number"
                  placeholder={t('optional')}
                  value={formData.budget_max}
                  onChange={(e) => setFormData({ ...formData, budget_max: e.target.value })}
                  className="mt-1"
                  data-testid="job-budget-max-input"
                />
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/dashboard/jobs')}
                className="flex-1 rounded-full"
              >
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#0044CC] hover:bg-[#003399] text-white rounded-full"
                disabled={loading}
                data-testid="submit-job-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('createJob')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateJobPage;
